/**
 * Zustand 认证 store
 * 持久化 accessToken 到 localStorage (key: mailman-auth)
 * user 对象不持久化，每次启动时由 bootstrap 从后端重新拉取
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import apiClient, { unwrap } from '@/api/client'
import { oauthApi } from '@/api/oauth'
import type { AuthUser, LoginResponse } from '@/api/types'

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  /** 续期用 refresh token；由 401 静默续期(api/client)消费 */
  refreshToken: string | null
  loading: boolean
  bootstrapped: boolean

  isAuthenticated: () => boolean
  login: (username: string, password: string, captchaToken: string, captchaX: number) => Promise<void>
  /** SSO / OAuth 代理登录:provider 为 oauth providers 列表里的 name */
  oauthLogin: (provider: string, username: string, password: string) => Promise<void>
  /** 标准授权码流程:用一次性 code 换回 token 并登录 */
  oauthExchangeLogin: (code: string) => Promise<void>
  logout: () => void
  setSession: (token: string, user: AuthUser) => void
  bootstrap: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      loading: false,
      bootstrapped: false,

      isAuthenticated: () => !!get().accessToken && !!get().user,

      login: async (username: string, password: string, captchaToken: string, captchaX: number) => {
        set({ loading: true })
        try {
          const data = await unwrap<LoginResponse>(
            apiClient.post('/auth/login', {
              username,
              password,
              captcha_token: captchaToken,
              captcha_x: captchaX,
            }),
          )
          set({
            user: data.user,
            accessToken: data.token,
            refreshToken: data.refresh_token,
            loading: false,
            bootstrapped: true,
          })
        } catch (e) {
          set({ loading: false })
          throw e
        }
      },

      /** OAuth 代理登录:中继到 IdP 后签发 mailman JWT(与本地登录同构) */
      oauthLogin: async (provider: string, username: string, password: string) => {
        set({ loading: true })
        try {
          const data = await oauthApi.login(provider, username, password)
          set({
            user: data.user,
            accessToken: data.token,
            refreshToken: data.refresh_token,
            loading: false,
            bootstrapped: true,
          })
        } catch (e) {
          set({ loading: false })
          throw e
        }
      },

      /** 标准授权码流程:用一次性 code 换回 token 并登录（gitman 模式） */
      oauthExchangeLogin: async (code: string) => {
        set({ loading: true })
        try {
          const data = await oauthApi.exchange(code)
          set({
            user: data.user,
            accessToken: data.token,
            refreshToken: data.refresh_token,
            loading: false,
            bootstrapped: true,
          })
        } catch (e) {
          set({ loading: false })
          throw e
        }
      },

      logout: () => {
        set({ user: null, accessToken: null, refreshToken: null, bootstrapped: true })
        localStorage.removeItem('mailman-auth')
      },

      setSession: (token: string, user: AuthUser) => {
        set({ user, accessToken: token, bootstrapped: true })
      },

      bootstrap: async () => {
        const token = get().accessToken
        if (!token) {
          set({ bootstrapped: true })
          return
        }
        try {
          // 用 token 调 /health 验证是否有效
          await apiClient.get('/health')
          // token 有效，但我们需要恢复 user 信息
          // 由于后端没有 /me 端点，从 JWT 解析 user 信息
          const payload = JSON.parse(atob(token.split('.')[1]))
          set({
            user: {
              id: payload.sub,
              username: payload.username,
              email: '',
              is_admin: true,
            },
            bootstrapped: true,
          })
        } catch {
          // token 无效，清除
          set({ user: null, accessToken: null, bootstrapped: true })
        }
      },
    }),
    {
      name: 'mailman-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      // 旧版本 localStorage 只有 accessToken；缺 refreshToken 视为未登录态兼容
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<AuthState>),
      }),
    },
  ),
)
